import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import {
  FormGroup,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { generatePageNumbers } from "utils/generatePageNumbers";

const CustomTableProducts = ({
  columns,
  data,
  currentPage,
  totalPages,
  onPageChange,
  isLoading,
  rowSize = 10,
  minWidth = "auto",
  selectedRowIds,
  setSelectedRowIds,
}) => {
  const { t } = useTranslation("tables");
  const enhancedColumns = useMemo(() => {
    const selectionColumn = {
      id: "selection",
      Header: ({ getToggleAllPageRowsSelectedProps }) => {
        const { key, ...checkboxProps } = getToggleAllPageRowsSelectedProps();
        return (
          <FormGroup check>
            <Label check>
              <Input type="checkbox" {...checkboxProps} />
              <span className="form-check-sign">
                <span className="check" />
              </span>
            </Label>
          </FormGroup>
        );
      },
      Cell: ({ row }) => {
        const { key, ...checkboxProps } = row.getToggleRowSelectedProps();
        return (
          <FormGroup check>
            <Label check>
              <Input type="checkbox" {...checkboxProps} />
              <span className="form-check-sign">
                <span className="check" />
              </span>
            </Label>
          </FormGroup>
        );
      },
      width: 50,
    };

    return [selectionColumn, ...columns];
  }, [columns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { selectedRowIds: tableSelectedRowIds },
    dispatch,
  } = useTable(
    {
      columns: enhancedColumns,
      data,
      initialState: {
        pageIndex: currentPage - 1,
        selectedRowIds: selectedRowIds.reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {}),
      },

      manualPagination: true,
      pageCount: totalPages,
      autoResetSelectedRows: false,
      getRowId: (row) => row?.uuid,
      stateReducer: (newState, action, prevState) => {
        if (action.type === "customAction") {
          return {
            ...prevState,
            selectedRowIds: action,
          };
        }
      },
    },
    useSortBy,
    usePagination,
    useRowSelect
  );
  useEffect(() => {
    if (selectedRowIds.length === 0) {
      dispatch({ type: "customAction", payload: {} });
    }
  }, [selectedRowIds]);
  useEffect(() => {
    const selectedIds = Object.keys(tableSelectedRowIds)
      .filter((id) => tableSelectedRowIds[id])
      .filter((id) => id !== "type" && id !== "payload");
    setSelectedRowIds((prevSelected) =>
      JSON.stringify(prevSelected) !== JSON.stringify(selectedIds)
        ? selectedIds
        : prevSelected
    );
  }, [tableSelectedRowIds]);

  const handlePageClick = (e, page) => {
    e.preventDefault();
    if (page >= 1 && page <= totalPages && !isLoading) {
      onPageChange(page);
    }
  };

  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("white-content")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("white-content"));
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const { key: tableKey, ...tableProps } = getTableProps();

  return (
    <>
      <div className="table-responsive">
        <table
          key={tableKey}
          {...tableProps}
          style={{ width: "100%", minWidth: minWidth, tableLayout: "fixed" }}
          className="table table-striped"
        >
          <thead>
            {headerGroups?.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers?.map((column) => {
                    const sortByProps = column.getSortByToggleProps
                      ? column.getSortByToggleProps()
                      : {};
                    const { key: sortKey, ...restSortProps } = sortByProps;
                    const { key: columnKey, ...headerProps } =
                      column.getHeaderProps(restSortProps);

                    return (
                      <th
                        key={columnKey}
                        {...headerProps}
                        style={{
                          inlineSize: column.width || "auto",
                        }}
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              <SkeletonTheme
                baseColor={isDarkMode ? "#ebebeb" : "#2c2c3a"}
                highlightColor={isDarkMode ? "#f5f5f5" : "#3a3a4a"}
                borderRadius="0.5rem"
                duration={1.4}
              >
                {Array?.from({ length: rowSize })?.map((_, rowIndex) => (
                  <tr key={`skeleton-row-${rowIndex}`}>
                    {Array.from({ length: columns.length + 1 })?.map(
                      (_, colIndex) => (
                        <td key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                          <Skeleton />
                        </td>
                      )
                    )}
                  </tr>
                ))}
              </SkeletonTheme>
            ) : data?.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center">
                  {t("noRecords")}
                </td>
              </tr>
            ) : (
              page?.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps}>
                    {row?.cells?.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td key={cellKey} {...cellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <nav
          className="d-flex justify-content-end"
          aria-label={t("pagination")}
        >
          <Pagination listClassName="mb-0">
            {/* Previous Button */}
            <PaginationItem disabled={isLoading || currentPage === 1}>
              <PaginationLink
                onClick={(e) => handlePageClick(e, currentPage - 1)}
              >
                <span aria-hidden={true}>
                  <i className="tim-icons icon-double-left" />
                </span>
              </PaginationLink>
            </PaginationItem>

            {/* Page Numbers */}
            {generatePageNumbers(currentPage, totalPages).map((page, index) => (
              <PaginationItem
                key={`page-${index}`}
                disabled={isLoading || page === "..."}
                active={currentPage === page}
              >
                <PaginationLink
                  onClick={(e) => page !== "..." && handlePageClick(e, page)}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}

            {/* Next Button */}
            <PaginationItem disabled={isLoading || currentPage === totalPages}>
              <PaginationLink
                onClick={(e) => handlePageClick(e, currentPage + 1)}
              >
                <span aria-hidden={true}>
                  <i className="tim-icons icon-double-right" />
                </span>
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </nav>
      )}
    </>
  );
};

export default CustomTableProducts;
